<template>
  <div :class="rootClass">
    <div class="dash-card bg-white">
      <div class="dash-card-head">
        <div>
          <div class="dash-card-title filson-black-family">Your appointments</div>
          <div class="dash-card-sub inter-regular-family text-muted">
            Bookings assigned to you{{ rolePhrase }} — accept to confirm the visit or decline if you cannot take it. After the service, use <strong>Mark complete</strong> so the job appears under <strong>Earnings</strong>.
          </div>
        </div>
        <button
          v-if="bookings.length && !loading"
          type="button"
          class="btn btn-sm btn-outline-secondary inter-regular-family"
          :disabled="loading"
          @click="load"
        >
          Refresh
        </button>
      </div>
      <div v-if="loading" class="dash-card-body inter-regular-family text-muted py-4">Loading appointments…</div>
      <div v-else-if="!bookings.length" class="dash-card-body inter-regular-family text-muted py-4">
        No appointments assigned to you yet. When an admin assigns a booking to your account, it will appear here.
      </div>
      <div v-else class="table-responsive px-3 pb-3">
        <table class="table table-sm align-middle mb-0 partner-appt-table">
          <thead class="text-muted small">
            <tr>
              <th>Booking</th>
              <th>Customer</th>
              <th>Visit</th>
              <th>Assignment</th>
              <th>Job</th>
              <th class="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="b in bookings"
              :key="b.id"
              :class="{
                'table-warning': assignmentStatusNormalized(b) === 'pending',
                'table-light': isJobCompleted(b),
              }"
            >
              <td class="fw-semibold">
                #{{ b.id }}
                <div v-if="b.website_name" class="small text-muted fw-normal">{{ b.website_name }}</div>
              </td>
              <td>
                {{ b.customer_name || '—' }}
                <div class="small text-muted">{{ b.customer_phone || '' }}</div>
                <div v-if="b.customer_address" class="small text-muted mt-1 visit-address">{{ b.customer_address }}</div>
              </td>
              <td>
                <div class="fw-semibold text-body">{{ formatVisitDate(b.appointment_date) }}</div>
                <div class="small text-muted">{{ timeWindow(b) }}</div>
              </td>
              <td>
                <span class="badge" :class="statusBadgeClass(b.partner_assignment_status)">
                  {{ statusLabel(b.partner_assignment_status) }}
                </span>
                <div v-if="assignedAgoLabel(b)" class="small text-muted mt-1">{{ assignedAgoLabel(b) }}</div>
              </td>
              <td>
                <span v-if="isJobCompleted(b)" class="badge bg-dark">Completed</span>
                <span v-else-if="assignmentStatusNormalized(b) === 'accepted'" class="badge bg-info text-dark">In progress</span>
                <span v-else class="text-muted small">—</span>
              </td>
              <td class="text-end text-nowrap">
                <template v-if="isPending(b)">
                  <button type="button" class="btn btn-sm btn-success me-1" :disabled="actingId === b.id" @click="accept(b)">
                    Accept appointment
                  </button>
                  <button type="button" class="btn btn-sm btn-outline-danger" :disabled="actingId === b.id" @click="decline(b)">
                    Decline
                  </button>
                </template>
                <template v-else-if="canMarkComplete(b)">
                  <button
                    type="button"
                    class="btn btn-sm btn-primary"
                    :disabled="actingId === b.id"
                    @click="markComplete(b)"
                  >
                    Mark complete
                  </button>
                </template>
                <template v-else-if="isJobCompleted(b)">
                  <span class="small text-muted">Done</span>
                </template>
                <template v-else>
                  <span class="small text-muted">—</span>
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PartnerAssignedBookingsCard',
  props: {
    /** @type {'groomer' | 'salon' | ''} */
    partnerRole: {
      type: String,
      default: '',
    },
    /** Full-width inside dashboard layout (no grid column wrapper). */
    standalonePage: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      bookings: [],
      actingId: null,
    };
  },
  computed: {
    rootClass() {
      return this.standalonePage ? 'partner-appt-standalone' : 'col-12 mt-3';
    },
    rolePhrase() {
      if (this.partnerRole === 'groomer') return ' as a groomer';
      if (this.partnerRole === 'salon') return ' as a salon';
      return '';
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    assignmentStatusNormalized(b) {
      const s = String(b?.partner_assignment_status || 'pending').toLowerCase().trim();
      if (s === 'accepted' || s === 'declined') return s;
      return 'pending';
    },
    isPending(b) {
      return this.assignmentStatusNormalized(b) === 'pending';
    },
    isJobCompleted(b) {
      return String(b?.booking_status || '').toLowerCase().trim() === 'completed';
    },
    canMarkComplete(b) {
      return this.assignmentStatusNormalized(b) === 'accepted' && !this.isJobCompleted(b);
    },
    formatVisitDate(dateStr) {
      if (!dateStr) return '—';
      const raw = String(dateStr).trim();
      const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) {
        const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
        return d.toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
      }
      const d = new Date(raw);
      return Number.isNaN(d.getTime()) ? raw : d.toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
    },
    timeWindow(b) {
      const parts = [b.duration_from, b.duration_to].filter(Boolean);
      return parts.length ? parts.join(' – ') : '';
    },
    statusLabel(status) {
      const s = String(status || 'pending').toLowerCase().trim();
      if (s === 'accepted') return 'Accepted';
      if (s === 'declined') return 'Declined';
      return 'Awaiting your response';
    },
    statusBadgeClass(status) {
      const s = String(status || 'pending').toLowerCase().trim();
      if (s === 'accepted') return 'bg-success';
      if (s === 'declined') return 'bg-danger';
      return 'bg-warning text-dark';
    },
    assignedAgoLabel(b) {
      const iso = b?.assigned_partner_at;
      if (!iso) return '';
      const t = new Date(iso).getTime();
      if (Number.isNaN(t)) return '';
      const sec = Math.max(0, Math.round((Date.now() - t) / 1000));
      if (sec < 60) return 'Assigned just now';
      const min = Math.floor(sec / 60);
      if (min < 60) return `Assigned ${min} minute${min === 1 ? '' : 's'} ago`;
      const hr = Math.floor(min / 60);
      if (hr < 24) return `Assigned ${hr} hour${hr === 1 ? '' : 's'} ago`;
      const day = Math.floor(hr / 24);
      if (day < 30) return `Assigned ${day} day${day === 1 ? '' : 's'} ago`;
      const month = Math.floor(day / 30);
      return `Assigned ${month} month${month === 1 ? '' : 's'} ago`;
    },
    async load() {
      this.loading = true;
      try {
        const res = await this.$axios.get('/partner/assigned-bookings');
        this.bookings = Array.isArray(res?.data?.bookings) ? res.data.bookings : [];
      } catch (_) {
        this.bookings = [];
      } finally {
        this.loading = false;
      }
    },
    async accept(b) {
      await this.act(b, 'accept');
    },
    async decline(b) {
      if (!window.confirm('Decline this appointment? The admin may assign it to someone else.')) return;
      await this.act(b, 'decline');
    },
    async markComplete(b) {
      if (!window.confirm('Mark this booking as completed? It will appear on your Earnings page.')) return;
      this.actingId = b.id;
      try {
        await this.$axios.post(`/partner/assigned-bookings/${b.id}/complete`);
        this.$toast?.success?.('Marked as completed.');
        await this.load();
      } catch (e) {
        const msg = e?.response?.data?.message || 'Could not update booking.';
        this.$toast?.error?.(msg);
      } finally {
        this.actingId = null;
      }
    },
    async act(b, verb) {
      this.actingId = b.id;
      try {
        await this.$axios.post(`/partner/assigned-bookings/${b.id}/${verb}`);
        this.$toast?.success?.(verb === 'accept' ? 'Appointment accepted.' : 'Appointment declined.');
        await this.load();
      } catch (e) {
        const msg = e?.response?.data?.message || 'Could not update assignment.';
        this.$toast?.error?.(msg);
      } finally {
        this.actingId = null;
      }
    },
  },
};
</script>

<style scoped>
.dash-card {
  border-radius: 14px;
  padding: 16px 16px 14px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.06);
}
.dash-card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}
.dash-card-title {
  font-size: 16px;
}
.dash-card-sub {
  font-size: 12px;
  line-height: 1.45;
  margin-top: 4px;
  max-width: 520px;
}
.visit-address {
  max-width: 280px;
  white-space: normal;
  word-break: break-word;
}
.partner-appt-table tbody tr.table-warning {
  --bs-table-bg: rgba(255, 193, 7, 0.12);
}
.partner-appt-standalone {
  width: 100%;
}

@media (max-width: 576px) {
  .dash-card {
    padding: 14px 12px 12px;
  }
  .dash-card-head {
    flex-wrap: wrap;
  }
  .dash-card-sub {
    max-width: none;
  }
  .visit-address {
    max-width: none;
  }
  /* Make action buttons easier to tap on small screens */
  td.text-end.text-nowrap > .btn {
    margin-top: 6px;
  }
}
</style>
